import React from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import './BreathingExercises.css';

const BreathingExercises = () => {
  const breathingTechniques = [
    {
      name: "Box Breathing",
      pattern: "4-4-4-4",
      description: "Equal timing for inhale, hold, exhale, hold",
      benefits: "Reduces stress and improves focus",
      icon: "fas fa-square"
    },
    {
      name: "4-7-8 Breathing",
      pattern: "4-7-8",
      description: "Inhale for 4, hold for 7, exhale for 8",
      benefits: "Promotes relaxation and better sleep",
      icon: "fas fa-moon"
    },
    {
      name: "Diaphragmatic",
      pattern: "Deep belly",
      description: "Focus on expanding the diaphragm",
      benefits: "Improves oxygen efficiency",
      icon: "fas fa-expand-arrows-alt"
    },
    {
      name: "Square Freediving",
      pattern: "Equal phases",
      description: "Specialized for freediving preparation",
      benefits: "Enhances breath-hold capacity",
      icon: "fas fa-swimmer"
    },
    {
      name: "CO2 Tables",
      pattern: "Progressive",
      description: "Builds CO2 tolerance for longer breath holds",
      benefits: "Improves freediving performance",
      icon: "fas fa-chart-line"
    },
    {
      name: "O2 Tables",
      pattern: "Oxygen focused",
      description: "Enhances oxygen efficiency training",
      benefits: "Increases breath-hold duration",
      icon: "fas fa-atom"
    },
    {
      name: "Wim Hof Method",
      pattern: "Power breathing",
      description: "Rapid breathing followed by retention",
      benefits: "Boosts energy and cold tolerance",
      icon: "fas fa-fire"
    },
    {
      name: "Tactical Breathing",
      pattern: "Combat tested",
      description: "Used by military and first responders",
      benefits: "Maintains calm under pressure",
      icon: "fas fa-shield-alt"
    }
  ];

  return (
    <section className="breathing-exercises py-5" id="breathing-exercises">
      <Container>
        <div className="text-center mb-5" data-aos="fade-up">
          <h2 className="display-4 fw-bold mb-3" style={{ color: 'var(--text-primary)' }}>
            Breathing Exercises
          </h2>
          <p className="lead mb-4" style={{ color: 'var(--text-secondary)' }}>
            Master your breath with professional breathing techniques designed for divers and freedivers
          </p>
        </div>

        <Row className="mb-5" data-aos="fade-up" data-aos-delay="100">
          <Col lg={6} className="mx-auto text-center">
            <div className="breathing-hero-card p-4 rounded-3">
              <div className="breathing-circle mb-3">
                <div className="circle-progress">
                  <svg width="120" height="120" className="progress-ring">
                    <circle
                      cx="60"
                      cy="60"
                      r="50"
                      fill="none"
                      stroke="rgba(255,255,255,0.2)"
                      strokeWidth="4"
                    />
                    <circle
                      cx="60"
                      cy="60"
                      r="50"
                      fill="none"
                      stroke="rgba(255, 255, 255, 0.95)"
                      strokeWidth="4"
                      strokeDasharray="314"
                      strokeDashoffset="157"
                      className="progress-circle"
                    />
                  </svg>
                  <div className="circle-text">
                    <span className="phase-text inhale-text">INHALE</span>
                    <span className="phase-text exhale-text">EXHALE</span>
                  </div>
                </div>
              </div>
              <h4 className="text-white mb-2">Interactive Training</h4>
              <p className="text-light mb-0">
                Follow along with visual guides and real-time breathing cues
              </p>
            </div>
          </Col>
        </Row>

        <Row className="g-4">
          {breathingTechniques.map((technique, index) => (
            <Col md={6} lg={3} key={index} data-aos="fade-up" data-aos-delay={150 + index * 100}>
              <Card className="h-100 breathing-card" style={{ color: 'var(--text-primary)' }}>
                <Card.Body className="d-flex flex-column">
                  <div className="breathing-icon mb-3">
                    <div className="icon-circle">
                      <i className={technique.icon}></i>
                    </div>
                  </div>
                  <Card.Title className="h5 mb-2" style={{ color: 'var(--text-primary)' }}>
                    {technique.name}
                  </Card.Title>
                  <div className="pattern-badge mb-2">
                    <span className="badge">
                      {technique.pattern}
                    </span>
                  </div>
                  <Card.Text className="mb-3 flex-grow-1" style={{ color: 'var(--text-secondary)' }}>
                    {technique.description}
                  </Card.Text>
                  <div className="benefits-section">
                    <small className="fw-semibold" style={{ color: 'var(--success-color)' }}>
                      <i className="fas fa-check-circle me-1"></i>
                      {technique.benefits}
                    </small>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        <Row className="mt-5" data-aos="fade-up" data-aos-delay="600">
          <Col lg={8} className="mx-auto">
            <div className="features-grid">
              <Row className="g-4">
                <Col md={4} className="text-center">
                  <div 
                    className="feature-icon-wrapper mb-3"
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '80px'
                    }}
                  >
                    <i 
                      className="fas fa-stopwatch"
                      style={{ 
                        color: 'var(--secondary-color)',
                        fontSize: '2.5rem'
                      }}
                    ></i>
                  </div>
                  <h5 
                    className="mb-3"
                    style={{ 
                      color: 'var(--text-primary)',
                      fontWeight: '600',
                      lineHeight: '1.3'
                    }}
                  >
                    Precise Timing
                  </h5>
                  <p 
                    className="mb-0" 
                    style={{ 
                      color: 'var(--text-secondary)',
                      fontSize: '0.95rem',
                      lineHeight: '1.5',
                      maxWidth: '250px',
                      margin: '0 auto'
                    }}
                  >
                    10-second preparation countdown with customizable cycle counts
                  </p>
                </Col>
                <Col md={4} className="text-center">
                  <div 
                    className="feature-icon-wrapper mb-3"
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '80px'
                    }}
                  >
                    <i 
                      className="fas fa-play-circle"
                      style={{ 
                        color: 'var(--secondary-color)',
                        fontSize: '2.5rem'
                      }}
                    ></i>
                  </div>
                  <h5 
                    className="mb-3"
                    style={{ 
                      color: 'var(--text-primary)',
                      fontWeight: '600',
                      lineHeight: '1.3'
                    }}
                  >
                    Full Control
                  </h5>
                  <p 
                    className="mb-0" 
                    style={{ 
                      color: 'var(--text-secondary)',
                      fontSize: '0.95rem',
                      lineHeight: '1.5',
                      maxWidth: '250px',
                      margin: '0 auto'
                    }}
                  >
                    Pause, resume, and stop controls for personalized sessions
                  </p>
                </Col>
                <Col md={4} className="text-center">
                  <div 
                    className="feature-icon-wrapper mb-3"
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '80px'
                    }}
                  >
                    <i 
                      className="fas fa-chart-line"
                      style={{ 
                        color: 'var(--secondary-color)',
                        fontSize: '2.5rem'
                      }}
                    ></i>
                  </div>
                  <h5 
                    className="mb-3"
                    style={{ 
                      color: 'var(--text-primary)',
                      fontWeight: '600',
                      lineHeight: '1.3'
                    }}
                  >
                    Progress Tracking
                  </h5>
                  <p 
                    className="mb-0" 
                    style={{ 
                      color: 'var(--text-secondary)',
                      fontSize: '0.95rem',
                      lineHeight: '1.5',
                      maxWidth: '250px',
                      margin: '0 auto'
                    }}
                  >
                    Visual feedback and phase-by-phase instructions
                  </p>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>

        <div className="text-center mt-5" data-aos="fade-up" data-aos-delay="700">
          <div className="info-box p-4 rounded-3">
            <h5 className="mb-3" style={{ color: 'var(--text-primary)' }}>
              <i 
                className="fas fa-info-circle me-2"
                style={{ color: 'var(--secondary-color)' }}
              ></i>
              Professional Training
            </h5>
            <p className="mb-0" style={{ color: 'var(--text-secondary)' }}>
              These breathing exercises are based on proven techniques used by professional divers, 
              freedivers, and athletes. Start with basic techniques and gradually progress to advanced methods.
            </p>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default BreathingExercises;
